import { WORD_STATE } from "../utils/words";
import { DailyMessage } from "../components/child/DailyMessage";

const SPIRAL_BG = `repeating-linear-gradient(to bottom, transparent, transparent 18px, #374151 18px, #374151 22px, transparent 22px, transparent 40px)`;
const LINE_BG = `repeating-linear-gradient(transparent, transparent 31px, #e0e7ff 31px, #e0e7ff 32px)`;
const TAPE_STYLE = { background: "linear-gradient(135deg, rgba(255,235,180,0.9) 0%, rgba(255,220,150,0.9) 100%)" };
const WASHI_STYLE = { background: "repeating-linear-gradient(45deg, #fecaca, #fecaca 10px, #fca5a5 10px, #fca5a5 20px)" };

function DoneBadge() {
  return (
    <div style={{
      position: "absolute", top: -8, right: -8,
      background: "linear-gradient(135deg, #22c55e, #16a34a)",
      color: "white", borderRadius: 9999, padding: "4px 10px",
      fontSize: 11, fontWeight: 700, transform: "rotate(12deg)",
      boxShadow: "0 2px 4px rgba(0,0,0,0.2)", zIndex: 10,
      fontFamily: "'Quicksand', sans-serif",
    }}>Done!</div>
  );
}

function WeeklyChart({ sessions }) {
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Today"];
  const today = new Date();
  const COLORS = ["#f472b6", "#fb923c", "#94a3b8", "#34d399", "#38bdf8", "#94a3b8", "#fbbf24"];
  const counts = days.map((_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() - (6 - i));
    const ds = d.toDateString();
    return sessions.filter(s => new Date(s.date).toDateString() === ds).length;
  });
  const max = Math.max(...counts, 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", height: 96, gap: 4 }}>
      {days.map((d, i) => {
        const h = Math.max(20, Math.round((counts[i] / max) * 80));
        const isToday = i === 6;
        return (
          <div key={d} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flex: 1 }}>
            <div style={{
              width: "100%", borderRadius: "4px 4px 0 0",
              height: h,
              background: counts[i] > 0 ? COLORS[i] : "#e5e7eb",
              border: counts[i] === 0 ? "2px dashed #d1d5db" : "none",
              outline: isToday ? "2px solid #f59e0b" : "none",
              outlineOffset: 2,
            }} />
            <span style={{
              fontFamily: "'Caveat', cursive", fontSize: 13,
              color: isToday ? "#d97706" : "#6b7280",
              fontWeight: isToday ? 700 : 400,
            }}>{d}</span>
          </div>
        );
      })}
    </div>
  );
}

export function StorybookHomeLayout({
  user, data,
  onStartTest, onStartMeaningTest, onStartSynonymTest, onStartAntonymTest,
  settings,
}) {
  const mySessions = data.sessions.filter(s => s.userId === user.id);
  const today = new Date().toDateString();
  const doneSpelling = mySessions.some(s => new Date(s.date).toDateString() === today && s.type !== "meaning" && s.type !== "synonym" && s.type !== "antonym");
  const doneMeaning  = mySessions.some(s => new Date(s.date).toDateString() === today && s.type === "meaning");
  const doneSynonym  = mySessions.some(s => new Date(s.date).toDateString() === today && s.type === "synonym");
  const doneAntonym  = mySessions.some(s => new Date(s.date).toDateString() === today && s.type === "antonym");
  const streak = data.streaks?.[user.id] || 0;
  const myPoints = data.points?.[user.id] || 0;
  const myGoalId = data.goals?.[user.id] || null;
  const myGoal = myGoalId ? (data.rewards || []).find(r => r.id === myGoalId) : null;
  const myProgress = Object.entries(data.progress || {}).filter(([k]) => k.startsWith(user.id) && !k.includes("_meaning_"));
  const mastered = myProgress.filter(([, v]) => v.state === WORD_STATE.MASTERED || v.mastered).length;
  const spellingAcc = (() => {
    const ss = mySessions.filter(s => s.type !== "meaning" && s.type !== "synonym" && s.type !== "antonym" && typeof s.score === "number");
    return ss.length ? Math.round(ss.reduce((a, s) => a + s.score, 0) / ss.length) : 0;
  })();

  const dateStr = new Date().toLocaleDateString("en-AU", { weekday: "long", month: "long", day: "numeric" });
  const appName = settings?.appName || "SpellWise Kids";

  const CARDS = [
    { label: "Spelling",  done: doneSpelling, onClick: onStartTest,        rotate: "-1deg",  bg: "linear-gradient(135deg, #38bdf8, #3b82f6)", tapeStyle: { ...TAPE_STYLE, transform: "rotate(3deg)", right: -4, top: -8, position: "absolute", width: 48, height: 16, borderRadius: 2 } },
    { label: "Meanings",  done: doneMeaning,  onClick: onStartMeaningTest,  rotate: "1deg",   bg: "linear-gradient(135deg, #a78bfa, #8b5cf6)", tapeStyle: { ...WASHI_STYLE, position: "absolute", top: -4, left: "50%", transform: "translateX(-50%)", width: 64, height: 12, borderRadius: 2 } },
    { label: "Synonyms",  done: doneSynonym,  onClick: onStartSynonymTest,  rotate: "-2deg",  bg: "linear-gradient(135deg, #34d399, #0d9488)", tapeStyle: { ...TAPE_STYLE, transform: "rotate(-2deg)", left: -4, top: -8, position: "absolute", width: 56, height: 16, borderRadius: 2 } },
    { label: "Antonyms",  done: doneAntonym,  onClick: onStartAntonymTest,  rotate: "2deg",   bg: "linear-gradient(135deg, #fb7185, #ef4444)", tapeStyle: { ...TAPE_STYLE, transform: "rotate(3deg)", right: 8, top: -8, position: "absolute", width: 56, height: 16, borderRadius: 2 } },
  ];

  return (
    <div style={{ maxWidth: 480, margin: "0 auto", position: "relative", minHeight: "100vh", fontFamily: "'Quicksand', sans-serif" }}>
      <style>{`
        .sb-content { padding: 24px 20px 24px 32px; }
        .sb-polaroid-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 32px; }
        @media (max-width: 359px) {
          .sb-content { padding: 20px 12px 20px 26px; }
          .sb-polaroid-grid { gap: 8px; }
        }
      `}</style>

      {/* Spiral binding — purely decorative overlay, does NOT consume layout width */}
      <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 18, background: SPIRAL_BG, zIndex: 10, pointerEvents: "none" }} />

      {/* Main notebook page — full width, spiral overlaps first 18px */}
      <div style={{ background: "white", minHeight: "100vh", position: "relative", paddingBottom: 32 }}>

        {/* Ruled lines */}
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none", backgroundImage: LINE_BG, backgroundPosition: "0 80px" }} />

        {/* Red margin line — sits just past the spiral */}
        <div style={{ position: "absolute", left: 24, top: 0, bottom: 0, width: 1, background: "#fca5a5" }} />

        {/* Content */}
        <div className="sb-content" style={{ position: "relative", zIndex: 1 }}>

          {/* Date + title */}
          <div style={{ position: "relative", marginBottom: 12, marginTop: 8 }}>
            <div style={{ ...TAPE_STYLE, position: "absolute", left: -8, top: 8, width: 72, height: 22, borderRadius: 2, opacity: 0.8 }} />
            <p style={{ color: "#9ca3af", fontSize: 13 }}>{dateStr}</p>
            <h1 style={{ fontFamily: "'Caveat', cursive", fontSize: 40, color: "#1f2937", marginTop: 4, lineHeight: 1 }}>{user.name}'s Journal</h1>
          </div>

          {/* Daily message — rotating motivational quote styled as a journal sticker */}
          <DailyMessage userName={user.name} streak={streak} bgGradient="linear-gradient(135deg, #f472b6 0%, #f43f5e 100%)" />

          {/* Stats row */}
          <div style={{ display: "flex", gap: 24, marginBottom: 32, flexWrap: "wrap" }}>
            {[
              { val: streak, label: "day streak!", color: "#d97706", bg: "#fef3c7", border: "#fbbf24" },
              { val: mastered, label: "words learned", color: "#059669", bg: "#d1fae5", border: "#34d399" },
              { val: `${spellingAcc}%`, label: "accuracy", color: "#2563eb", bg: "#dbeafe", border: "#60a5fa" },
            ].map(({ val, label, color, bg, border }) => (
              <div key={label} style={{ textAlign: "center" }}>
                <div style={{ width: 64, height: 64, borderRadius: "50%", background: bg, border: `4px dashed ${border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontFamily: "'Caveat', cursive", fontSize: 22, color, fontWeight: 700 }}>{val}</span>
                </div>
                <p style={{ fontFamily: "'Caveat', cursive", fontSize: 16, color: "#6b7280", marginTop: 4 }}>{label}</p>
              </div>
            ))}
          </div>

          {/* Today's Adventures heading */}
          <h2 style={{ fontFamily: "'Caveat', cursive", fontSize: 28, color: "#374151", marginBottom: 16, textDecoration: "underline wavy #f9a8d4" }}>Today's Adventures</h2>

          {/* Polaroid grid */}
          <div className="sb-polaroid-grid">
            {CARDS.map(({ label, done, onClick, rotate, bg, tapeStyle }) => (
              <div
                key={label}
                onClick={onClick}
                style={{
                  background: "white", padding: "12px 12px 40px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.12)", border: "1px solid #e5e7eb",
                  borderRadius: 4, position: "relative", cursor: "pointer",
                  transform: `rotate(${rotate})`,
                  opacity: done ? 0.7 : 1,
                  transition: "transform .2s",
                }}
              >
                {done && <DoneBadge />}
                <div style={{ tapeStyle }} />
                <div style={{ tapeStyle: undefined, ...tapeStyle }} />
                <div style={{ background: bg, height: 80, borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {done
                    ? <svg style={{ width: 48, height: 48 }} fill="white" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
                    : <svg style={{ width: 40, height: 40, color: "rgba(255,255,255,0.7)" }} fill="none" stroke="white" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                  }
                </div>
                <p style={{ fontFamily: "'Caveat', cursive", fontSize: 20, color: "#374151", position: "absolute", bottom: 8, left: 12 }}>{label}</p>
              </div>
            ))}
          </div>

          {/* Weekly chart */}
          <div style={{ marginBottom: 32 }}>
            <h2 style={{ fontFamily: "'Caveat', cursive", fontSize: 28, color: "#374151", marginBottom: 16, textDecoration: "underline wavy #6ee7b7" }}>My Progress</h2>
            <div style={{ background: "#fffbeb", border: "2px dashed #fcd34d", borderRadius: 12, padding: 16 }}>
              <WeeklyChart sessions={mySessions} />
            </div>
          </div>

          {/* Goal sticky note */}
          {myGoal && (
            <div style={{
              background: "#fef08a", padding: 16,
              boxShadow: "2px 4px 8px rgba(0,0,0,0.15)",
              transform: "rotate(-1deg)", marginBottom: 32,
              clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%)",
              position: "relative",
            }}>
              <h3 style={{ fontFamily: "'Caveat', cursive", fontSize: 22, color: "#1f2937", marginBottom: 8 }}>🎯 My Goal</h3>
              <p style={{ color: "#374151", fontSize: 14, marginBottom: 8 }}>{myGoal.emoji} {myGoal.name}</p>
              <div style={{ background: "#fde047", borderRadius: 999, height: 12, overflow: "hidden" }}>
                <div style={{ height: "100%", background: "#22c55e", borderRadius: 999, width: `${Math.min(100, Math.round((myPoints / myGoal.cost) * 100))}%`, transition: "width .5s" }} />
              </div>
              <p style={{ fontFamily: "'Caveat', cursive", fontSize: 18, color: "#4b5563", marginTop: 4 }}>⭐ {myPoints} / {myGoal.cost} pts</p>
            </div>
          )}

          {/* Version footer */}
          <p style={{ fontFamily: "'Caveat', cursive", fontSize: 14, color: "#d1d5db", textAlign: "center" }}>{appName}</p>

        </div>

        {/* Page curl */}
        <div style={{ position: "absolute", bottom: 0, right: 0, width: 80, height: 80, background: "linear-gradient(135deg, transparent 70%, #e5e7eb 70%, #d1d5db 100%)", pointerEvents: "none" }} />
      </div>
    </div>
  );
}
